import {
  AdditiveBlending,
  BoxGeometry,
  CapsuleGeometry,
  CircleGeometry,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  MeshToonMaterial,
  PlaneGeometry,
  SphereGeometry,
  type Texture,
} from 'three'

import type { CharacterKind } from '@/components'
import { clamp, clamp01 } from '@/core/math/mathUtils'
import type { CharacterVisualState } from '@/gfx/SceneBridge'
import { BODY_PLANS, type BodyPlan } from './characterKinds'
import { HEAD_PROFILES, TORSO_PROFILES, inkOutline, lathe, toonMaterial } from './toon'
import { Spring, SpringAngle } from './spring'

/**
 * A fighter: a cartoon humanoid — ball head, rounded body, two-segment legs —
 * built from primitives and animated entirely in code. No skeleton, no clips;
 * the pose is a pure function of the simulation state, so it can never
 * desynchronise from the simulation.
 *
 * The animation model is written down in docs/CHARACTERS.md. The three things
 * worth knowing here, all of them measured rather than chosen:
 *
 *  - **Step frequency comes from a law, not a constant.** The old rig used a
 *    fixed spatial frequency, which pinned the stride at 1.01 m at every speed
 *    and put the hip at ±7° while walking — 2.6 pixels on this camera, which is
 *    the number behind "there is no animation".
 *  - **Feet do not slide.** During stance the foot travels backward in body
 *    space at exactly the body's speed, so contact is exact by construction.
 *  - **Poses are sprung, not damped.** An exponential approach cannot overshoot,
 *    and overshoot is where the sense of weight lives.
 */

const GEO = {
  head: new SphereGeometry(1, 14, 11),
  headBox: new BoxGeometry(1.6, 1.5, 1.5),
  body: new CapsuleGeometry(1, 1, 5, 12),
  bodyBox: new BoxGeometry(2, 1.6, 1.6),
  limb: new CapsuleGeometry(1, 1, 3, 7),
  hand: new SphereGeometry(1, 8, 6),
  eye: new SphereGeometry(1, 10, 8),
  glow: new PlaneGeometry(2.1, 2.1),
  shield: new SphereGeometry(0.85, 16, 12),
}

/** Angular plans use boxes for the head and torso; the rest use capsules. */
const BOXY: Partial<Record<CharacterKind, boolean>> = { brick: true, guard: true }

/** Counts rigs so each one starts its idle animation on a different beat. */
let rigsBuilt = 0

// ── the gait law ───────────────────────────────────────────────────────────

/**
 * Steps per second at a given speed: `f = 2.23 + 0.68·v`.
 *
 * Fitted to measured human cadence scaled to this rig's 0.44 m leg by dynamic
 * similarity (frequencies × 1.43). Walking spends added speed roughly half on
 * cadence and half on stride; running spends almost all of it on stride, and a
 * single line covers both well enough at this size. See docs/CHARACTERS.md §2.2.
 */
function stepsPerSecond(speed: number): number {
  return 2.23 + 0.68 * speed
}

/** Fraction of a step spent with the foot on the ground: 0.62 → 0.24. */
function stanceFraction(speed: number): number {
  return clamp(0.64 - 0.057 * speed, 0.24, 0.64)
}

/**
 * Hip swing amplitude in radians.
 *
 * Peaks around 3–4 m/s and comes *down* again toward a sprint, because at speed
 * the contact time collapses and the leg spends its cycle in flight. That shape
 * is the whole difference between the old linear ramp — 24 % of the needed
 * amplitude at a walk, 147 % at a sprint — and something that reads as running.
 */
function hipAmplitude(speed: number): number {
  const table = [
    [0, 0],
    [0.5, 0.278],
    [1, 0.506],
    [2, 0.782],
    [3, 0.908],
    [4, 0.911],
    [5, 0.832],
    [6, 0.716],
    [7, 0.578],
  ]
  if (speed <= 0) return 0
  for (let i = 1; i < table.length; i++) {
    if (speed > table[i][0] && i < table.length - 1) continue
    const [x0, y0] = table[i - 1]
    const [x1, y1] = table[i]
    const t = clamp01((speed - x0) / (x1 - x0))
    return y0 + (y1 - y0) * t
  }
  return table[table.length - 1][1]
}

/**
 * Foot lift profile over the swing phase, peaking early and settling into the
 * plant. A symmetric arc makes the foot stamp; this one lands softly.
 */
function stepArc(u: number): number {
  return 9.4815 * (1 - u) ** 3 * u
}

// ── the rig ────────────────────────────────────────────────────────────────

export class CharacterRig {
  readonly group = new Group()

  private readonly pivot = new Group()
  private readonly body = new Group()
  private readonly head: Mesh
  private readonly face = new Group()
  /** The piece that breathes. On a stacked torso it is the middle crate. */
  private readonly torso!: Mesh
  /** Hip joints; the shin hangs from a knee joint inside each. */
  private readonly legL: Group
  private readonly legR: Group
  private readonly kneeL: Group
  private readonly kneeR: Group
  private readonly armL: Group
  private readonly armR: Group
  private readonly eyes: Mesh[] = []
  /** The lower pair on a four-armed plan; empty otherwise. */
  private readonly extraArms: Group[] = []
  private readonly glow: Mesh
  private readonly aura: Mesh
  private readonly crown: Mesh
  private readonly braceShield: Mesh
  private readonly pickupShield: Mesh
  private readonly bodyMaterials: MeshToonMaterial[] = []
  private readonly eyeMaterial: MeshBasicMaterial

  private readonly color: Color
  private readonly plan: BodyPlan
  private readonly thigh: number
  private readonly shin: number

  /** Step phase in cycles; the two legs are half a cycle apart. */
  private phase: number
  private clock: number

  // Sprung poses. Overshoot and settle is what reads as mass.
  private readonly lean = new Spring(0, 0.18, 0.65)
  private readonly crouch = new Spring(0, 0.12, 0.9)
  private readonly tumble = new Spring(0, 0.16, 0.8)
  private readonly lookYaw = new SpringAngle(0, 0.22, 0.7)
  private readonly armTrail = new Spring(0, 0.24, 0.55)
  /** Vertical squash from a landing; 1 is neutral. */
  private readonly squash = new Spring(1, 0.14, 0.5)

  private blinkTimer: number
  private blinkPhase = 0
  private lastBounces = 0
  private weightShift = 0
  private weightTimer: number
  private portrait = false

  constructor(color: number, glowTexture: Texture, kind: CharacterKind = 'guard') {
    this.color = new Color(color)
    const plan = BODY_PLANS[kind]
    this.plan = plan
    const boxy = BOXY[kind] === true

    // Each rig starts on its own beat: six identical breathing bodies read as a
    // rendering glitch rather than as six people.
    const seed = rigsBuilt++
    this.phase = (seed * 0.37) % 1
    this.clock = (seed * 0.61) % 6.28
    this.blinkTimer = 1.5 + (seed % 5) * 0.8
    this.weightTimer = 3 + (seed % 4) * 1.5

    // Height is built from segment lengths, never from a scale on the whole
    // subtree: scaling the group turned the tall plan's head into an egg and
    // the short one's into a disc, which is what "стянутый по вертикали" was.
    const stretch = plan.height
    const legLength = plan.legLength * stretch
    const bodyLength = plan.bodyLength * stretch
    this.thigh = legLength * 0.52
    this.shin = legLength * 0.48

    const bodyColor = this.color.clone()
    const headColor = this.color.clone().lerp(new Color(0xffffff), 0.22)
    const limbColor = this.color.clone().multiplyScalar(0.72)

    const bodyMat = this.material(bodyColor)
    const headMat = this.material(headColor)
    const limbMat = this.material(limbColor)

    // ── torso ────────────────────────────────────────────────────────────
    // Construction, not proportion. A stack of crates is not a wide capsule and
    // an ovoid is not a narrow one: they are different objects, and that is the
    // whole point — six variants of one capsule read as one character six times.
    // Bodies are lathed from a profile, so the radius changes along the torso —
    // a chest that narrows into a waist, a carapace widest low down. Scaling a
    // capsule cannot produce either, and scaling a capsule is what every
    // previous version of this roster did.
    const torsoBase = legLength * 0.94
    if (plan.torso === 'stack') {
      for (let i = 0; i < 3; i++) {
        const crate = new Mesh(GEO.bodyBox, bodyMat)
        const shrink = 1 - i * 0.15
        crate.scale.set(plan.bodyWidth * shrink, bodyLength * 0.4, plan.bodyWidth * shrink)
        crate.position.y = legLength + bodyLength * (0.22 + i * 0.44)
        crate.rotation.y = (i % 2 === 0 ? 1 : -1) * 0.2
        this.body.add(this.inked(crate, 0.04))
        if (i === 1) this.torso = crate
      }
    } else {
      const profile = TORSO_PROFILES[plan.torso] ?? TORSO_PROFILES.capsule
      this.torso = new Mesh(lathe(profile, 18), bodyMat)
      this.torso.scale.set(
        plan.bodyWidth * 1.35,
        bodyLength * 1.5,
        plan.bodyWidth * (plan.torso === 'ovoid' ? 1.6 : 1.35),
      )
      this.torso.position.y = torsoBase
      this.body.add(this.inked(this.torso))
    }

    // ── head ─────────────────────────────────────────────────────────────
    // Lifted to leave a visible neck: with the head sitting on the shoulders
    // the silhouette was one continuous blob, and a blob has no pose.
    const headY = legLength + bodyLength * 1.12 + plan.head * 1.05
    const headProfile = HEAD_PROFILES[plan.headShape] ?? HEAD_PROFILES.sphere
    this.head = new Mesh(lathe(headProfile, boxy ? 6 : 16), headMat)
    // The profile spans one unit tall and one unit in radius, so an even head
    // needs height = 2 × width. Anything else is a tube wearing a face.
    const headTall = plan.headShape === 'egg' ? 2.7 : 1.95
    this.head.scale.set(plan.head * 1.15, plan.head * headTall, plan.head * 1.15)
    // Lathed shapes stand on their base, so the head hangs from the neck rather
    // than being centred on it.
    this.head.position.y = headY - plan.head
    if (boxy) this.head.rotation.y = Math.PI / 6
    this.body.add(this.inked(this.head, 0.045))

    // The face is a group tilted back toward the crown. Sitting on the equator
    // it was invisible from 45 % of directions — and this camera looks down at
    // 52°, so a fighter walking away simply had no face at all.
    this.face.position.y = headY
    this.face.rotation.x = -FACE_TILT
    this.body.add(this.face)

    this.eyeMaterial = new MeshBasicMaterial({ color: 0x111417, toneMapped: false })
    for (const side of [-1, 1]) {
      const eye = new Mesh(GEO.eye, this.eyeMaterial)
      eye.scale.setScalar(plan.head * EYE_RADIUS)
      eye.position.set(side * plan.head * 0.34, plan.head * 0.1, plan.head * 0.9)
      this.face.add(eye)
      this.eyes.push(eye)
    }

    // ── limbs ────────────────────────────────────────────────────────────
    this.legL = new Group()
    this.legR = new Group()
    this.kneeL = this.buildLeg(this.legL, limbMat, -plan.stance, legLength)
    this.kneeR = this.buildLeg(this.legR, limbMat, plan.stance, legLength)
    // A hovering fighter has no legs at all — and therefore no walk cycle. It
    // is a different mode of locomotion, which is what makes it a different
    // character rather than a shorter one.
    if (plan.legs === 'none') {
      this.legL.visible = false
      this.legR.visible = false
    }

    // The arms hang clear of the torso and splay slightly outward. A limb that
    // runs along the edge of the body disappears into the silhouette, and the
    // silhouette is all this camera has: the authored shoulder offset (0.27)
    // was inside the body width (0.26) on the default plan, so the swing was
    // invisible however far it swung.
    const shoulderX = plan.bodyWidth + plan.limb * 1.9
    const shoulderY = legLength + bodyLength * 1.05

    if (plan.arms === 'wings') {
      // Flat plates rather than sticks: a bird's outline is its wings, and at
      // this size a wing has to be an area, not a line.
      this.armL = this.makeWing(limbMat, plan, -1, shoulderX, shoulderY, stretch)
      this.armR = this.makeWing(limbMat, plan, 1, shoulderX, shoulderY, stretch)
    } else {
      const length = plan.armLength * stretch * (plan.arms === 'stubs' ? 0.45 : 1)
      const thickness = plan.limb * (plan.arms === 'stubs' ? 1.5 : 0.9)
      this.armL = this.makeLimb(limbMat, thickness, length, -shoulderX, shoulderY)
      this.armR = this.makeLimb(limbMat, thickness, length, shoulderX, shoulderY)
      this.armL.rotation.z = 0.14
      this.armR.rotation.z = -0.14
    }

    // A second, smaller pair lower down. Four arms is a creature, not a variant.
    if (plan.arms === 'four') {
      for (const side of [-1, 1]) {
        const extra = this.makeLimb(
          limbMat,
          plan.limb * 0.62,
          plan.armLength * stretch * 0.6,
          side * (shoulderX * 0.85),
          legLength + bodyLength * 0.62,
        )
        extra.rotation.z = side * 0.75
        extra.rotation.x = -0.5
        this.body.add(extra)
        this.extraArms.push(extra)
      }
    }
    this.body.add(this.legL, this.legR, this.armL, this.armR)

    this.pivot.add(this.body)
    this.group.add(this.pivot)

    // ── ground glow ──────────────────────────────────────────────────────
    this.glow = new Mesh(
      GEO.glow,
      new MeshBasicMaterial({
        map: glowTexture,
        color: this.color,
        transparent: true,
        opacity: 0.7,
        blending: AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    this.glow.rotation.x = -Math.PI / 2
    this.glow.position.y = 0.02
    this.glow.scale.setScalar(1.25)
    this.group.add(this.glow)

    // Instability halo: invisible at 0 %, an angry corona at 100 %.
    this.aura = new Mesh(
      GEO.glow,
      new MeshBasicMaterial({
        map: glowTexture,
        color: new Color(0xff3a1a),
        transparent: true,
        opacity: 0,
        blending: AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    this.aura.rotation.x = -Math.PI / 2
    this.aura.position.y = 0.04
    this.aura.scale.setScalar(0.85)
    this.group.add(this.aura)

    // A shaped token on the crown, in the player's colour. Colour alone fails:
    // the acid and yellow slots are 3.7 ΔE apart for a deuteranope — the same
    // fighter, twice. The crown is the one surface visible from every angle.
    this.crown = new Mesh(
      crownGeometry(kind),
      new MeshBasicMaterial({
        color: this.color.clone().lerp(new Color(0xffffff), 0.35),
        toneMapped: false,
        side: 2,
      }),
    )
    this.crown.rotation.x = -Math.PI / 2
    // Clear of the skull, not embedded in it: at 0.92 the disc sat inside the
    // sphere and showed only as a dark ring where the two intersected.
    this.crown.position.y = headY + plan.head * 1.08
    // Big enough to name the slot from across the arena, small enough that the
    // head is still a head: at 1.5 it read as a dinner plate worn as a hat.
    this.crown.scale.setScalar(plan.head * 0.9)
    this.body.add(this.crown)

    this.buildFeature(plan, headMat, limbMat, headY, legLength, bodyLength)

    this.braceShield = this.makeShield(0xdcd6c4, true)
    this.pickupShield = this.makeShield(color, true)
    this.pickupShield.scale.setScalar(1.15)
    this.group.add(this.braceShield, this.pickupShield)
  }

  /**
   * The one silhouette element that names this fighter.
   *
   * Everything here is built to be legible as an outline rather than as detail:
   * a bar across the face, a dome on the back, a wedge on the front of the head.
   * At twenty-two pixels tall nothing else survives, and detail that does not
   * survive is detail that makes six fighters look like one.
   */
  private buildFeature(
    plan: BodyPlan,
    headMat: MeshToonMaterial,
    limbMat: MeshToonMaterial,
    headY: number,
    legLength: number,
    bodyLength: number,
  ): void {
    const dark = new MeshStandardMaterial({
      color: new Color(0x11151a),
      roughness: 0.5,
      metalness: 0.1,
      emissive: new Color(0x000000),
    })

    switch (plan.feature) {
      case 'visor': {
        // A dark slit across the face, unmistakable head-on and in profile.
        const visor = new Mesh(GEO.headBox, dark)
        visor.scale.set(plan.head * 1.02, plan.head * 0.3, plan.head * 1.02)
        visor.position.y = headY + plan.head * 0.18
        this.body.add(visor)
        break
      }
      case 'shell': {
        // A domed carapace over the back, plus a pair of antennae above it.
        const shell = new Mesh(GEO.head, headMat)
        shell.scale.set(plan.bodyWidth * 1.5, plan.bodyWidth * 1.1, plan.bodyWidth * 1.7)
        shell.position.set(0, legLength + bodyLength * 0.75, -plan.bodyWidth * 0.35)
        shell.castShadow = true
        this.body.add(shell)
        for (const side of [-1, 1]) {
          const antenna = new Mesh(GEO.limb, limbMat)
          antenna.scale.set(plan.limb * 0.4, plan.head * 0.55, plan.limb * 0.4)
          antenna.position.set(side * plan.head * 0.35, headY + plan.head * 1.15, 0)
          antenna.rotation.z = side * 0.5
          this.body.add(antenna)
        }
        break
      }
      case 'beak': {
        const beak = new Mesh(GEO.limb, dark)
        beak.scale.set(plan.head * 0.3, plan.head * 0.55, plan.head * 0.3)
        beak.position.set(0, headY - plan.head * 0.1, plan.head * 0.95)
        beak.rotation.x = Math.PI / 2.1
        this.body.add(beak)
        // A neck, which is the other half of what makes this one a crane.
        const neck = new Mesh(GEO.limb, headMat)
        neck.scale.set(plan.limb * 1.1, plan.head * 0.8, plan.limb * 1.1)
        neck.position.y = headY - plan.head * 1.1
        this.body.add(neck)
        break
      }
      case 'plate': {
        const plate = new Mesh(GEO.bodyBox, dark)
        plate.scale.set(plan.bodyWidth * 0.95, bodyLength * 0.45, plan.bodyWidth * 0.35)
        plate.position.set(0, legLength + bodyLength * 0.62, plan.bodyWidth * 0.8)
        this.body.add(plate)
        break
      }
      case 'horns': {
        for (const side of [-1, 1]) {
          const horn = new Mesh(GEO.limb, limbMat)
          horn.scale.set(plan.limb * 0.9, plan.head * 0.75, plan.limb * 0.9)
          horn.position.set(side * plan.head * 0.5, headY + plan.head * 0.95, 0)
          horn.rotation.z = side * 0.85
          horn.castShadow = true
          this.body.add(horn)
        }
        break
      }
      default:
        break
    }
  }

  /**
   * A banded toon material. Continuous shading on a bare primitive is what made
   * every previous roster read as "two capsules and legs": the eye gets a
   * smooth gradient and calls it a shape, not a character. See `toon.ts`.
   */
  private material(color: Color): MeshToonMaterial {
    const mat = toonMaterial(color)
    this.bodyMaterials.push(mat)
    return mat
  }

  /** Adds a mesh to the body with its own contour. */
  private inked(mesh: Mesh, thickness?: number): Mesh {
    inkOutline(mesh, thickness)
    mesh.castShadow = true
    return mesh
  }

  private makeShield(color: number, wireframe: boolean): Mesh {
    const mesh = new Mesh(
      GEO.shield,
      new MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0,
        blending: AdditiveBlending,
        depthWrite: false,
        side: 2,
        wireframe,
        toneMapped: false,
      }),
    )
    mesh.position.y = 0.7
    mesh.visible = false
    return mesh
  }

  /** A flat wing plate, hinged at the shoulder. */
  private makeWing(
    material: MeshToonMaterial,
    plan: BodyPlan,
    side: number,
    x: number,
    jointY: number,
    stretch: number,
  ): Group {
    const joint = new Group()
    joint.position.set(side * x, jointY, 0)

    const span = plan.armLength * stretch
    const wing = new Mesh(GEO.bodyBox, material)
    wing.scale.set(span * 0.9, plan.limb * 1.4, span * 0.55)
    wing.position.set(side * span * 0.45, -span * 0.1, 0)
    joint.add(this.inked(wing, 0.05))

    const tip = new Mesh(GEO.bodyBox, material)
    tip.scale.set(span * 0.5, plan.limb * 1.1, span * 0.32)
    tip.position.set(side * span * 0.95, -span * 0.22, -span * 0.1)
    tip.rotation.z = side * 0.3
    joint.add(this.inked(tip, 0.05))

    joint.rotation.z = side * -0.25
    return joint
  }

  /** A limb hanging from a pivot at `jointY`, offset sideways by `x`. */
  private makeLimb(
    material: MeshToonMaterial,
    thickness: number,
    length: number,
    x: number,
    jointY: number,
  ): Group {
    const joint = new Group()
    joint.position.set(x, jointY, 0)

    const bone = new Mesh(GEO.limb, material)
    bone.scale.set(thickness, length * 0.5, thickness)
    bone.position.y = -length * 0.5
    joint.add(this.inked(bone, 0.09))

    const hand = new Mesh(GEO.hand, material)
    hand.scale.setScalar(thickness * 1.5)
    hand.position.y = -length
    joint.add(this.inked(hand, 0.09))

    return joint
  }

  /** Two-segment leg: hip group with a knee group inside it. Returns the knee. */
  private buildLeg(
    hip: Group,
    material: MeshToonMaterial,
    x: number,
    legLength: number,
  ): Group {
    hip.position.set(x, legLength, 0)

    const thighMesh = new Mesh(GEO.limb, material)
    thighMesh.scale.set(this.plan.limb, this.thigh * 0.5, this.plan.limb)
    thighMesh.position.y = -this.thigh * 0.5
    hip.add(this.inked(thighMesh, 0.09))

    const knee = new Group()
    knee.position.y = -this.thigh
    hip.add(knee)

    const shinMesh = new Mesh(GEO.limb, material)
    shinMesh.scale.set(this.plan.limb * 0.92, this.shin * 0.5, this.plan.limb * 0.92)
    shinMesh.position.y = -this.shin * 0.5
    knee.add(this.inked(shinMesh, 0.09))

    const foot = new Mesh(GEO.hand, material)
    foot.scale.set(this.plan.limb * 1.5, this.plan.limb * 1.1, this.plan.limb * 2.2)
    foot.position.set(0, -this.shin, this.plan.limb * 0.7)
    knee.add(this.inked(foot, 0.09))

    return knee
  }

  // ────────────────────────────────────────────────────────────── update ──

  update(state: CharacterVisualState, dt: number): void {
    if (this.portrait) return
    this.clock += dt

    const speed = state.speed
    const airborne = state.airborne || state.falling

    if (airborne) this.poseAirborne(state, dt)
    else this.poseGrounded(state, dt)

    // Posture targets: lean into a run, crouch behind a brace, dive on a dash.
    let leanTarget = clamp01(speed / 7) * 0.26
    let crouchTarget = 0
    if (state.bracing) {
      leanTarget = 0.36
      crouchTarget = 0.16
    }
    if (state.dashing) {
      leanTarget = 0.66
      crouchTarget = 0.08
    }
    // A cast plants the body: the windup is a snap, not an arc — 50 ms is three
    // frames, and a smoothed anticipation inside it reads as input lag.
    if (state.castPhase === 'windup') crouchTarget += 0.1
    if (state.castPhase === 'active') leanTarget += 0.3
    if (airborne) {
      leanTarget = -0.5
      crouchTarget = 0
    }

    this.lean.set(leanTarget)
    this.crouch.set(crouchTarget)
    this.tumble.set(airborne ? clamp01(state.launchSpeed / 24) : 0)
    this.lean.step(dt)
    this.crouch.step(dt)
    this.tumble.step(dt)

    // Landing squash, one impulse per contact with the slab. The number of
    // bounces is how the strength of a hit reads after the impact frame, so it
    // gets the loudest body cue the rig has.
    if (state.bounces > this.lastBounces) {
      const strength = clamp01(state.launchSpeed / 30)
      this.squash.reset(1 - (0.18 + strength * 0.24))
    }
    this.lastBounces = state.bounces
    this.squash.set(1)
    this.squash.step(dt)

    // The resting hunch is part of who this fighter is, not part of the motion.
    this.pivot.rotation.x = this.lean.value + this.plan.hunch
    this.pivot.position.y = -this.crouch.value
    this.body.scale.y = this.squash.value
    this.body.scale.x = 1 / Math.sqrt(this.squash.value)
    this.body.scale.z = this.body.scale.x

    this.updateFace(state, dt)
    this.updateAuras(state, dt)
  }

  /** Walk, run and stand. */
  private poseGrounded(state: CharacterVisualState, dt: number): void {
    const speed = state.speed
    const plan = this.plan
    const moving = speed > 0.35

    // A hovering fighter has no gait to run: it floats, tips into its travel
    // and bobs. Nothing below this line would mean anything for it.
    if (plan.legs === 'none') {
      this.body.position.y =
        plan.bodyLength * plan.height * plan.hover + Math.sin(this.clock * 2.4) * 0.05
      this.body.rotation.z = Math.sin(this.clock * 1.7) * 0.09
      const tip = clamp01(speed / 9) * 0.3
      this.armTrail.set(Math.sin(this.clock * 3.1))
      this.armTrail.step(dt)
      this.armL.rotation.x = -0.5 - this.armTrail.value * 0.35 - tip
      this.armR.rotation.x = -0.5 + this.armTrail.value * 0.35 - tip
      this.torso.scale.y = plan.bodyLength * plan.height * (1 + Math.sin(this.clock * 2.4) * 0.03)
      return
    }

    const f = stepsPerSecond(speed) * plan.gaitRate
    const beta = stanceFraction(speed)
    const amp = hipAmplitude(speed) * plan.swing
    // Travel of the foot over one contact — by definition speed × stance time,
    // which is what makes the plant exact instead of approximately right.
    const contact = moving ? (speed * beta) / f : 0

    if (moving) this.phase = (this.phase + f * dt) % 1

    const hipY = plan.legLength * plan.height
    this.solveLeg(this.legL, this.kneeL, this.phase, beta, contact, amp, hipY, state)
    this.solveLeg(this.legR, this.kneeR, (this.phase + 0.5) % 1, beta, contact, amp, hipY, state)

    // Arms oppose the same-side leg, one swing per cycle, trailing slightly.
    const swing = moving ? Math.sin(this.phase * Math.PI * 2) : 0
    this.armTrail.set(swing)
    this.armTrail.step(dt)
    const armSwing = this.armTrail.value * amp * 0.62

    // Wings beat instead of swinging, and the lower pair on a four-armed plan
    // scuttles at twice the rate — different limbs, different motion.
    if (plan.arms === 'wings') {
      const beat = moving ? Math.sin(this.phase * Math.PI * 2) : Math.sin(this.clock * 1.6) * 0.25
      this.armL.rotation.z = -0.25 - beat * 0.5
      this.armR.rotation.z = 0.25 + beat * 0.5
    }
    for (let i = 0; i < this.extraArms.length; i++) {
      const side = i === 0 ? -1 : 1
      const beat = moving ? Math.sin(this.phase * Math.PI * 4 + i * Math.PI) : 0
      this.extraArms[i].rotation.x = -0.5 + beat * 0.45
      this.extraArms[i].rotation.z = side * 0.75
    }

    // A cast overrides the arms entirely: the pose has to say which ability.
    if (state.castPhase !== null) {
      const reach = state.castPhase === 'recover' ? 0.7 : 1
      this.armL.rotation.x = -1.5 * reach
      this.armR.rotation.x = -1.5 * reach
    } else if (state.bracing) {
      this.armL.rotation.x = -1.25
      this.armR.rotation.x = -1.25
    } else {
      this.armL.rotation.x = -armSwing
      this.armR.rotation.x = armSwing
    }

    // The vertical bob is two per cycle and, correctly, is *lowest* at double
    // support. It is under three pixels on this camera either way — kept for
    // correctness, deliberately not grown into a hop.
    const bob = moving ? Math.abs(Math.cos(this.phase * Math.PI * 2)) * 0.045 : 0

    // Idle: breathing, a slow sway and an occasional shift of weight. Standing
    // is what fighters do most of the time, and a statue is the single most
    // visible flaw in a rig this simple.
    let idleY = 0
    let idleRoll = 0
    if (!moving) {
      const breath = Math.sin(this.clock * 1.5) * 0.011
      idleY = breath
      idleRoll = Math.sin(this.clock * 1.1 + 1.3) * 0.03
      this.torso.scale.y = plan.bodyLength * plan.height * (1 + breath * 1.4)

      this.weightTimer -= dt
      if (this.weightTimer <= 0) {
        this.weightTimer = 4 + ((this.clock * 7) % 5)
        this.weightShift = this.weightShift > 0 ? -1 : 1
      }
      idleRoll += this.weightShift * 0.04
    } else {
      this.torso.scale.y = plan.bodyLength * plan.height
    }

    this.body.position.y = bob + idleY
    this.body.rotation.z = idleRoll
  }

  /**
   * Placement of one foot for the current step phase, solved to hip and knee
   * angles. Stance walks the foot backward at exactly the body's speed; swing
   * carries it forward along an early-peaking arc.
   */
  private solveLeg(
    hip: Group,
    knee: Group,
    phase: number,
    beta: number,
    contact: number,
    amp: number,
    hipY: number,
    state: CharacterVisualState,
  ): void {
    let footZ: number
    let footY: number

    if (contact <= 1e-4) {
      // Standing: a relaxed, slightly bent leg rather than a locked stick.
      footZ = 0
      footY = -hipY * 0.985
    } else if (phase < beta) {
      const u = phase / beta
      footZ = contact * (0.5 - u)
      footY = -hipY
    } else {
      const u = (phase - beta) / (1 - beta)
      // A little extrapolation forward, so the foot reaches for where the body
      // will be rather than for where it is.
      footZ = -contact * 0.5 + contact * 1.15 * u
      footY = -hipY + stepArc(u) * hipY * 0.22
    }

    // Amplitude scaling lets a plan exaggerate without breaking the contact.
    footZ *= amp / Math.max(0.2, hipAmplitude(state.speed) || 1)

    const reach = Math.min(Math.hypot(footZ, footY), (this.thigh + this.shin) * 0.99)
    const dir = Math.atan2(footZ, -footY)

    // Two-bone IK by the law of cosines: the angle at the hip between the leg
    // line and the thigh, and the interior angle at the knee.
    const cosHip = clamp(
      (this.shin ** 2 - this.thigh ** 2 - reach ** 2) / (-2 * this.thigh * reach),
      -1,
      1,
    )
    const cosKnee = clamp(
      (reach ** 2 - this.thigh ** 2 - this.shin ** 2) / (-2 * this.thigh * this.shin),
      -1,
      1,
    )
    // A bird's knee folds the other way. Mirroring both joints is the whole
    // difference between a person's leg and a crane's, and it changes the
    // silhouette far more than any proportion could.
    const back = this.plan.legs === 'digitigrade' ? -1 : 1
    hip.rotation.x = dir + Math.acos(cosHip) * back
    knee.rotation.x = (Math.PI - Math.acos(cosKnee)) * back * -1
  }

  /** Being thrown: a tucked, windmilling figure. */
  private poseAirborne(state: CharacterVisualState, dt: number): void {
    const t = this.tumble.value
    const spin = this.clock * (6 + clamp01(state.launchSpeed / 30) * 8)
    this.body.rotation.z = Math.sin(spin) * 0.42 * t
    this.body.position.y = 0
    this.armL.rotation.x = -1.6 - Math.sin(spin * 1.7) * 0.9 * t
    this.armR.rotation.x = -1.6 + Math.sin(spin * 1.9) * 0.9 * t
    this.legL.rotation.x = Math.sin(spin * 1.3) * 0.7 * t
    this.legR.rotation.x = -Math.sin(spin * 1.5) * 0.7 * t
    this.kneeL.rotation.x = -0.9 - Math.sin(spin * 1.1) * 0.4 * t
    this.kneeR.rotation.x = -0.9 + Math.sin(spin * 1.4) * 0.4 * t
    this.torso.scale.y = this.plan.bodyLength * this.plan.height
    this.armTrail.reset(0)
    void dt
  }

  /**
   * Head, gaze and eyes.
   *
   * Expression is carried by the *shape* of the eye, never by extra detail: the
   * head is nine pixels across in the harness and eighteen in play, and bloom
   * eats anything finer. Five shapes is what a five-pixel eye can hold, and it
   * is exactly what TABS gets by with.
   */
  private updateFace(state: CharacterVisualState, dt: number): void {
    // Gaze: turn toward the nearest opponent, bounded, so a fighter reads as
    // paying attention to somebody rather than staring through them.
    let yawTarget = 0
    if (state.lookX !== 0 || state.lookZ !== 0) {
      const want = Math.atan2(state.lookX, state.lookZ)
      let delta = want - state.angle
      while (delta > Math.PI) delta -= Math.PI * 2
      while (delta < -Math.PI) delta += Math.PI * 2
      yawTarget = clamp(delta, -1.05, 1.05)
    }
    this.lookYaw.set(state.falling || state.airborne ? 0 : yawTarget)
    this.lookYaw.step(dt)
    this.head.rotation.y = this.lookYaw.value
    this.face.rotation.y = this.lookYaw.value

    // Blink: 17 a minute at rest, 26 in a fight, and none at all mid-cast —
    // concentration suppresses blinking, which is why the wind-up stare reads
    // as intent. Closing is twice as fast as opening, as in a real blink.
    const busy = state.castPhase !== null || state.bracing
    this.blinkTimer -= dt * (busy ? 0 : state.speed > 1 ? 1.5 : 1)
    if (this.blinkTimer <= 0 && this.blinkPhase <= 0) {
      this.blinkPhase = BLINK_CLOSE + BLINK_OPEN
      this.blinkTimer = 2.2 + ((this.clock * 13) % 4)
    }
    if (this.blinkPhase > 0) this.blinkPhase = Math.max(0, this.blinkPhase - dt)

    let openness = 1
    if (this.blinkPhase > BLINK_OPEN) {
      openness = (this.blinkPhase - BLINK_OPEN) / BLINK_CLOSE
    } else if (this.blinkPhase > 0) {
      openness = 1 - this.blinkPhase / BLINK_OPEN
    }

    // Shape by state, all of it in two scale factors.
    let wide = 1
    let tall = 1
    let lift = 0
    if (state.stunned) {
      // Squeezed shut and crossed — the "X X" of every game that does this.
      wide = 1.25
      tall = 0.22
    } else if (state.falling || state.airborne) {
      wide = 1.2
      tall = 1.5
      lift = 0.05
    } else if (state.castPhase === 'windup' || state.castPhase === 'active') {
      wide = 0.85
      tall = 1.35
    } else if (state.bracing) {
      wide = 1.15
      tall = 0.55
    } else if (state.instability > 70) {
      wide = 1.1
      tall = 1.2
    }

    const base = this.plan.head * EYE_RADIUS
    for (const eye of this.eyes) {
      eye.scale.set(base * wide, base * tall * openness, base)
      eye.position.y = this.plan.head * (0.1 + lift)
    }
  }

  private updateAuras(state: CharacterVisualState, dt: number): void {
    // Instability drives every warning cue at once: hotter body, brighter halo.
    const heat = clamp01(state.instability / 100)
    const pulse = heat > 0.7 ? 0.7 + 0.3 * Math.sin(this.clock * 12) : 1
    for (const mat of this.bodyMaterials) {
      mat.emissiveIntensity = (0.5 + heat * 2.2) * pulse
    }
    const auraMat = this.aura.material as MeshBasicMaterial
    auraMat.opacity = heat * 0.55 * pulse
    this.aura.scale.setScalar(0.85 + heat * 0.5)

    const glowMat = this.glow.material as MeshBasicMaterial
    glowMat.opacity = state.falling ? 0 : 0.6 + (state.bracing ? 0.35 : 0)
    this.glow.visible = !state.falling
    this.glow.scale.setScalar(
      state.haste > 0 ? 1.45 + Math.sin(this.clock * 12) * 0.08 : 1.25,
    )
    this.crown.visible = !state.falling

    // Stance shield: bright and wide at full charge, tight and flickering as it
    // runs out, so "how long have I got" needs no glance at the HUD.
    this.braceShield.visible = state.bracing
    if (state.bracing) {
      const left = clamp01(state.braceFraction)
      const mat = this.braceShield.material as MeshBasicMaterial
      const dying = left < 0.25 ? 0.55 + 0.45 * Math.sin(this.clock * 35) : 1
      mat.opacity = (0.05 + left * 0.1) * dying
      this.braceShield.scale.setScalar(0.82 + left * 0.32)
      this.braceShield.rotation.y += dt * 1.6
    }

    this.pickupShield.visible = state.shield > 0
    if (state.shield > 0) {
      const mat = this.pickupShield.material as MeshBasicMaterial
      const dying =
        state.shield < 1.5 ? 0.4 + 0.6 * Math.abs(Math.sin(this.clock * 20)) : 1
      mat.opacity = 0.12 * dying
      this.pickupShield.rotation.y -= dt * 0.9
    }
  }

  /** A still, readable pose for the menu portraits. */
  setPortraitMode(): void {
    this.portrait = true
    this.glow.visible = false
    this.aura.visible = false
    this.braceShield.visible = false
    this.pickupShield.visible = false
    // A relaxed, slightly open stance reads better than a rigid T-pose.
    this.armL.rotation.x = 0.22
    this.armR.rotation.x = -0.18
    this.legL.rotation.x = 0.12
    this.legR.rotation.x = -0.14
    this.kneeL.rotation.x = -0.18
    this.kneeR.rotation.x = -0.24
  }

  dispose(): void {
    this.group.traverse((obj) => {
      const mesh = obj as Mesh
      if (!mesh.material) return
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      for (const m of mats) m.dispose()
    })
  }
}

/** How far the face is rolled back toward the crown, in radians (≈57°). */
const FACE_TILT = 1
/** Eye radius as a fraction of the head's. 0.19 measured out at 2.8 px. */
const EYE_RADIUS = 0.32
const BLINK_CLOSE = 0.08
const BLINK_OPEN = 0.17

/**
 * A distinct silhouette per fighter, worn on the crown.
 *
 * Shape rather than colour alone, because two of the six player colours are
 * 3.7 ΔE apart under deuteranopia — indistinguishable — and the crown is the
 * one part of a fighter this camera can see from every direction.
 */
function crownGeometry(kind: CharacterKind): CircleGeometry {
  return new CircleGeometry(0.5, BODY_PLANS[kind].token)
}
